import Phaser from "phaser";
import { GameState } from "../state/GameState";
import { Tex } from "../ui/textures";
import { makeButton } from "../ui/widgets";

/**
 * Campaign map: pick a market off a full-screen world map. Sits between the title
 * and the level select.
 *
 *  - EUROPEAN MARKET: always open. Its three levels live in LevelSelect. It is
 *    "won" once all nine stars are collected (3★ on all three levels).
 *  - AMERICAN MARKET: locked (padlock on the map) until Europe is won. Opening it
 *    runs the Kirby relay (Finale).
 *
 * The backdrop is a full-screen image with the whole picture swapping on hover:
 *  - Not won: region-eur-open (Europe white glow, USA locked). Hovering Europe
 *    swaps to region-eur-hover (Europe gold glow).
 *  - Won: region-usa-open (USA flag, Europe green + trophy). Hovering the USA
 *    swaps to region-usa-hover (USA gold glow).
 * The continents are invisible click zones over the artwork; the backdrops are a
 * clean 16:9 match so they fill the canvas 1:1.
 */

// Continent hit zones in game coordinates: [centerX, centerY, width, height].
const EUROPE_ZONE = { x: 525, y: 194, w: 172, h: 150 };
const USA_ZONE = { x: 234, y: 186, w: 214, h: 150 };

export class MarketSelectScene extends Phaser.Scene {
  constructor() {
    super("MarketSelect");
  }

  create() {
    const { width, height } = this.scale;
    const europeWon = GameState.finaleUnlocked(); // 3★ on all three = all 9 stars
    const americaWon = GameState.americaWon(); // the relay (American market) has been beaten

    // Full-screen backdrop; the whole image swaps on region hover. Once America is won
    // the map shows the both-conquered art; everything else stays the same.
    const baseKey = americaWon ? Tex.regionUsaWon : europeWon ? Tex.regionUsaOpen : Tex.regionEurOpen;
    const bg = this.add.image(width / 2, height / 2, baseKey).setDisplaySize(width, height);

    // Europe: always playable. Its gold-glow hover art only exists pre-win (once
    // won, Europe shows the trophy and simply stays put on hover).
    this.addRegion(EUROPE_ZONE, bg, baseKey, {
      enabled: true,
      hover: europeWon ? null : Tex.regionEurHover,
      onClick: () => this.scene.start("LevelSelect"),
    });

    // America: only once Europe is won; hover swaps to its gold-glow art. Once America
    // is conquered there is no hover state - it stays on the conquered art. Entering it
    // plays the pre-relay cutscene (Lucas/Dan dialogue -> Kirby's video call) before
    // the relay itself.
    this.addRegion(USA_ZONE, bg, baseKey, {
      enabled: europeWon,
      hover: europeWon && !americaWon ? Tex.regionUsaHover : null,
      onClick: () => this.scene.start("Dialogue", { scriptKey: "pre-relay", next: "KirbyCall", nextData: { next: "Relay" } }),
    });

    if (americaWon) {
      makeButton(this, width / 2, height - 60, "🎉  Celebrate", () => {
        this.scene.start("Dialogue", { scriptKey: "post-relay", next: "Ending" });
      }, { width: 260, fill: 0xc8961e });
    }

    if (import.meta.env.DEV) {
      makeButton(this, width / 2 - 230, 516, "DEV: unlock all ★", () => {
        GameState.grantEverything();
        this.scene.restart();
      }, { width: 210, height: 30, fontSize: "12px" });
      makeButton(this, width / 2, 516, "DEV: reset progress", () => {
        GameState.reset();
        this.scene.restart();
      }, { width: 210, height: 30, fontSize: "12px" });
      makeButton(this, width / 2 + 230, 516, "DEV: play relay", () => {
        this.scene.start("Relay");
      }, { width: 210, height: 30, fontSize: "12px" });
    }
  }

  /** An invisible click zone over a continent. When `hover` is set, entering the
   *  zone swaps the whole backdrop to that image and leaving restores `baseKey`. */
  private addRegion(
    zone: { x: number; y: number; w: number; h: number },
    bg: Phaser.GameObjects.Image,
    baseKey: string,
    o: { enabled: boolean; hover: string | null; onClick: () => void },
  ) {
    const rect = this.add.rectangle(zone.x, zone.y, zone.w, zone.h, 0x000000, 0);
    rect.setInteractive({ useHandCursor: o.enabled });
    if (o.hover) {
      rect.on("pointerover", () => bg.setTexture(o.hover as string));
      rect.on("pointerout", () => bg.setTexture(baseKey));
    }
    if (o.enabled) rect.on("pointerdown", o.onClick);
  }
}
